/**
 * Trasy sekcji ustawień — **jedno miejsce**, z którego korzysta i router aplikacji,
 * i test powłoki ustawień.
 *
 * ⚠ Powstało z naprawy dubletu (2026-08-06): sekcje były wypisane w `app/routes.tsx`
 * i **drugi raz** we własnej powłoce testu, więc każda nowa wymagała dwóch wpisów, a rozjazd
 * między nimi znaczył, że test bada inną aplikację niż ta wdrażana. Dziś obie strony biorą tę
 * samą tablicę i różnią się tylko tym, czym mają się różnić: aplikacja mocuje ją pod
 * `SettingsShell` (belka, powrót), test pod goły `SettingsScreen`.
 *
 * ⭐ Ekrany są przypisane **rekordem wyczerpującym** po `SettingsSectionPath`, a nie kolejną
 * listą: dopisanie sekcji w `model/sections.ts` bez ekranu jest wtedy błędem kompilacji,
 * a nie pustym miejscem odkrywanym po wdrożeniu.
 *
 * ⭐⭐ **Skład mieszka w `app/`, nie w `identity`** — i to jest naprawa cyklu, nie porządki.
 * Dopóki tablica stała w `identity`, każdy ekran ustawień należący do innego feature'u musiał
 * być stamtąd importowany; a gdy taki ekran potrzebował czegoś z `identity` (wyszukiwania
 * w organizacji przy nadawaniu właściciela), zależność zamykała się w pierścień. `app/` jest
 * jedyną warstwą, która **może** znać wszystkie feature'y — i to jest dokładnie jej rola.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, type RouteObject } from 'react-router-dom';
import { Lock } from 'lucide-react';

import { canSee, useSession } from '@/features/auth';
import { Skeleton } from '@/shared/ui';

import styles from './settingsRoutes.module.css';

import { ElementTypesSection } from '@/features/elements';
import { ImportScreen } from '@/features/import';
import { OwnerlessScreen } from '@/features/owners';
import {
  SETTINGS_GROUPS,
  AccountSection,
  InviteSection,
  TeamsSection,
  UsersSection,
} from '@/features/identity';
import type { SettingsSectionPath } from '@/features/identity';

const SECTION_SCREENS: Record<SettingsSectionPath, ReactElement> = {
  users: <UsersSection />,
  teams: <TeamsSection />,
  invite: <InviteSection />,
  'element-types': <ElementTypesSection />,
  import: <ImportScreen />,
  ownerless: <OwnerlessScreen />,
  account: <AccountSection />,
};

/*
 * ⭐ Trasy powstają z KLUCZY rekordu, a nie z przejścia po grupach — i to nie jest drobiazg
 * stylistyczny. Grupy są wystawiane pod interfejsem (konsumenci czytają `roles`), więc ich
 * `path` jest zwykłym `string`-iem i nie da się nim zaindeksować rekordu bez rzutowania.
 * Klucze rekordu są już dokładnie zbiorem `SettingsSectionPath`, czyli kompletnym.
 *
 * Skutek uboczny jest lepszy niż powód: test „trasa dla każdej sekcji" przestał być
 * tautologią. Dwie listy mają teraz osobne pochodzenie — jedna ze spisu grup, druga z rekordu
 * ekranów — więc porównanie ich naprawdę coś sprawdza.
 */
/**
 * Strażnik sekcji — ⛔ **sekcja bez uprawnień nie rysuje warsztatu**.
 *
 * ⭐⭐ Powstał ze znaleziska z audytu uprawnień (2026-08-05): sekcje były **ukrywane, nie
 * blokowane** (dec. 78), więc wejście ADRESEM w `/settings/element-types` dawało zwykłemu
 * userowi pełny zestaw „Dodaj / Zmień nazwę / Wycofaj / Usuń". Serwer trzymał linię i nic się
 * nie zapisywało — ale ukryliśmy **sekcję**, a nie **akcje w niej**, czyli tę samą regułę
 * złamaliśmy o poziom niżej.
 *
 * ⚠ To dalej **nie jest kontrola dostępu** — tę trzyma serwer przy każdym żądaniu (spec 9.2).
 * To jest konsekwencja: nie pokazujemy drzwi, które i tak są zamknięte, ani na liście, ani
 * po wpisaniu adresu.
 *
 * ⚠ Dopóki sesja się ładuje, `canSee` odpowiada „nie" — więc czekamy na nią szkieletem.
 * Bez tego każde wejście w ustawienia mrugałoby odmową, także uprawnionemu.
 */
function SectionGuard({ path, children }: { path: SettingsSectionPath; children: ReactElement }) {
  const { t } = useTranslation();
  const { data: user, isPending } = useSession();

  const roles = SETTINGS_GROUPS.flatMap((group) => group.sections).find(
    (section) => section.path === path,
  )?.roles;

  if (roles === undefined || roles.length === 0) {
    return children;
  }

  if (isPending) {
    return <Skeleton height="12rem" announce />;
  }

  if (canSee(user, roles)) {
    return children;
  }

  // ⚠ Komunikat mówi, czego brakuje CZYTAJĄCEMU, i NIE wylicza uprawnionych (dec. 96) —
  // taka forma nie może się zestarzeć przy zmianie ról.
  // ⭐ Odmowa DAJE WYJŚCIE: „konto i wygląd" jest jedyną sekcją bez ról, więc na pewno stoi
  // przed każdym otworem. Ślepy zaułek zmuszałby do cofania się przyciskiem przeglądarki.
  return (
    <div className={styles['denied']} role="alert">
      <p className={styles['deniedHead']}>
        <Lock size={16} aria-hidden="true" />
        {t('settings.sectionDenied')}
      </p>
      <p className={styles['deniedBody']}>{t('settings.sectionDeniedHint')}</p>
      <Link className={styles['deniedAction']} to="/settings/account">
        {t('settings.sectionDeniedAction')}
      </Link>
    </div>
  );
}

export const SETTINGS_SECTION_ROUTES: readonly RouteObject[] = [
  // ⚠ Wejście bez sekcji trafia na „moje konto", a nie na „użytkowników": ta pierwsza jest
  // jedyną dostępną dla KAŻDEGO, a nie-admin nie ma po co oglądać przekierowania na ekran,
  // którego i tak nie zobaczy.
  { index: true, element: <Navigate to="account" replace /> },
  ...Object.entries(SECTION_SCREENS).map(([path, element]) => ({
    path,
    element: (
      <SectionGuard path={path as SettingsSectionPath}>{element}</SectionGuard>
    ),
  })),
];
